import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconFlipHorizontal,
    IconFlipVertical,
    IconRotate2,
    IconRotate360,
    IconRotateClockwise,
    IconRotateClockwise2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import { videoTileClass, videoTrayClass } from '../../components/video/VideoOptionTiles';
import {
    DEFAULT_TRANSFORM,
    MAX_ROTATE_VIDEO_BYTES,
    ROTATE_TRANSFORMS,
    formatFileSize,
    isSupportedVideoFile,
    rotateVideo,
    rotateVideoName,
} from './rotateVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

// Same three rotate glyphs as the PDF rotate tool. Tabler names a flip by the
// mirror line it draws: IconFlipVertical has a vertical line (left ↔ right,
// ffmpeg's hflip), IconFlipHorizontal a horizontal one (top ↔ bottom, vflip).
const TRANSFORM_ICONS = {
    270: IconRotate2,
    90: IconRotateClockwise2,
    180: IconRotate360,
    hflip: IconFlipVertical,
    vflip: IconFlipHorizontal,
};

export default function RotateVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [transform, setTransform] = useState(DEFAULT_TRANSFORM);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('rotateVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_ROTATE_VIDEO_BYTES) {
            const msg = t('rotateVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setTransform(DEFAULT_TRANSFORM);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setTransform(DEFAULT_TRANSFORM);
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('rotateVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'ROTATE_FAILED') {
            return t('rotateVideo.errors.failed');
        }
        return t('rotateVideo.errors.failed');
    };

    const handleTransform = async () => {
        if (!file || processing) return;

        recordToolUsage('video/rotate', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await rotateVideo(file, transform, (value) => setProgress(value));
            const output = {
                name: rotateVideoName(file.name, transform),
                originalSize: file.size,
                outputSize: blob.size,
                transform,
                blob,
            };
            setResult(output);
            notifications.show({
                title: t('common.success'),
                message: t('rotateVideo.successNotice', { transform: t(`rotateVideo.transforms.${transform}`) }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`rotateVideo.howTo.step${i}`)),
        [t],
    );

    const transformLabel = t(`rotateVideo.transforms.${transform}`);

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({t(`rotateVideo.transforms.${result.transform}`)})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('rotateVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    const rotateOptions = ROTATE_TRANSFORMS.filter((item) => item.group === 'rotate');
    const flipOptions = ROTATE_TRANSFORMS.filter((item) => item.group === 'flip');

    // Icon only — the name is the tooltip / aria-label, and the pick is spelled
    // out in the "selected" line under both rows.
    const renderTransformButton = (item) => {
        const Icon = TRANSFORM_ICONS[item.id] || TRANSFORM_ICONS[DEFAULT_TRANSFORM];
        const active = transform === item.id;
        const label = t(`rotateVideo.transforms.${item.id}`);
        return (
            <button
                key={item.id}
                type="button"
                onClick={() => {
                    setTransform(item.id);
                    invalidateResult();
                }}
                title={label}
                aria-label={label}
                aria-pressed={active}
                className={videoTileClass(active)}
            >
                <Icon size={22} stroke={1.7} />
            </button>
        );
    };

    return (
        <ToolPageLayout
            slug="video/rotate"
            subtitleKey="rotateVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('rotateVideo.apply'),
                onClick: handleTransform,
                disabled: !file || processing || !!result,
                loading: processing,
                icon: <IconRotateClockwise size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('rotateVideo.dropTitle')}
                            hint={t('rotateVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('rotateVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('rotateVideo.settingsTitle')}>
                    <div className="space-y-4">
                        <div>
                            <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('rotateVideo.rotateTitle')}
                            </p>
                            <div className={videoTrayClass('grid-cols-3')}>
                                {rotateOptions.map(renderTransformButton)}
                            </div>
                        </div>

                        <div>
                            <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('rotateVideo.flipTitle')}
                            </p>
                            <div className={videoTrayClass('grid-cols-2')}>
                                {flipOptions.map(renderTransformButton)}
                            </div>
                        </div>

                        <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('rotateVideo.selectedLabel', { transform: transformLabel })}
                        </p>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('rotateVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('rotateVideo.previewEmpty')}
                    idleHint={t('rotateVideo.previewEmptyHint')}
                    processingLabel={t('rotateVideo.processing')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('rotateVideo.done')}
                    downloadLabel={t('rotateVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('rotateVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('rotateVideo.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : transformLabel}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
